import './AccountInformation.scss';

import { ButtonV2, InputField, Spinner, Toast, useGA4 } from '@travelwin/core';
import { Form, Formik, useFormikContext } from 'formik';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useOutletContext } from 'react-router';

import { ANALYTICS_EVENT_TYPES } from '../../../analytics';
import ChangePassword from '../../../components/ChangePassword';
import ChangePhoneNumber from '../../../components/ChangePhoneNumber';
import HelmetWrapper from '../../../components/HelmetWrapper';
import { useNavigate } from '../../../components/Link';
import PageTitle from '../../../components/PageTitle';
import { useUser } from '../../../context/User';

const className = 'p-AccountInformation';

interface UserProfileFormValues {
  name: string;
  surname: string;
  email: string;
  password: string;
  phoneNumber: string;
}

const SubmitButton = () => {
  const { t } = useTranslation();
  const { dirty, isSubmitting } = useFormikContext();

  return (
    <ButtonV2
      data-testid="button-save-changes"
      type="submit"
      variant="primary"
      size="large"
      disabled={!dirty || isSubmitting}
    >
      {t('account_information.save_changes', 'Save Changes')}{' '}
      {isSubmitting && <Spinner size={12} color="white" />}
    </ButtonV2>
  );
};

type OutletContextType = {
  setTitle: (title: ReactNode) => void;
};

const AccountInformation = () => {
  const { t } = useTranslation();
  const { user, updateNameAndSurname, refreshUser } = useUser();
  const { trackEvent } = useGA4();
  const navigate = useNavigate();
  const location = useLocation();
  const passwordChangedSuccess = location.state?.passwordChangedSuccess;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<UserProfileFormValues>({
    name: user?.given_name ?? '',
    surname: user?.family_name ?? '',
    email: user?.email ?? '',
    password: '********',
    phoneNumber: user?.phone_number ?? '',
  });

  const [isShowChangePasswordModal, setIsShowChangePasswordModal] =
    useState(false);
  const [isShowChangePhoneNumberModal, setIsShowChangePhoneNumberModal] =
    useState(false);

  const { setTitle } = useOutletContext<OutletContextType>() || {
    setTitle: () => {},
  };

  const backToMyAccount = () => {
    navigate('/my-account');
  };

  useEffect(() => {
    setTitle(
      <PageTitle
        data-testid="title-my-account-information"
        hasBackButton={true}
        onBackButtonClick={backToMyAccount}
        title={t('account_information.title', 'Account Information')}
      />,
    );
  }, []);

  const saveChanges = async (values: UserProfileFormValues): Promise<void> => {
    try {
      await updateNameAndSurname(values.name, values.surname);
      setInitialValues(values);
    } catch {
      setErrorMessage(
        t(
          'account_information.update_account_information.error',
          'Problem with saving account changes, please try again.',
        ),
      );
    }
  };

  const changePassword = () => {
    trackEvent(ANALYTICS_EVENT_TYPES.AccountChangePassword);
    setIsShowChangePasswordModal(true);
  };

  const renderChangePasswordModal = () => {
    if (!isShowChangePasswordModal) return null;

    const handleClose = () => {
      setIsShowChangePasswordModal(false);
    };

    return (
      <ChangePassword
        open={isShowChangePasswordModal}
        handleClose={handleClose}
      />
    );
  };

  const renderPasswordChangedSuccess = () => {
    return (
      <div className={`${className}__message_wrapper`}>
        <Toast>
          {t(
            'account_information.password_changed_success',
            'Your password was changed successfully.',
          )}
        </Toast>
      </div>
    );
  };

  const changePhoneNumber = () => {
    trackEvent(ANALYTICS_EVENT_TYPES.AccountChangeNumber);
    setIsShowChangePhoneNumberModal(true);
  };

  const renderChangePhoneNumberModal = () => {
    if (!isShowChangePhoneNumberModal) return null;

    const handleClose = async (phoneNumber?: string) => {
      setIsShowChangePhoneNumberModal(false);
      if (typeof phoneNumber === 'string') {
        await refreshUser();
        setInitialValues((prevValues) => ({
          ...prevValues,
          phoneNumber,
        }));
      }
    };

    return (
      <ChangePhoneNumber
        open={isShowChangePhoneNumberModal}
        handleClose={handleClose}
        addNew={!user?.phone_number}
      />
    );
  };

  return (
    <div className={className}>
      <HelmetWrapper
        title={t('browser.accountInformationTitle', 'Account Information')}
      />
      {passwordChangedSuccess && renderPasswordChangedSuccess()}
      <div className={`${className}__header`}>
        <h2 className={`${className}__title`}>
          {t('account_information.title', 'Account Information')}
        </h2>
      </div>
      <div className={`${className}__form-container`}>
        <Formik<UserProfileFormValues>
          initialValues={initialValues}
          enableReinitialize
          onSubmit={saveChanges}
        >
          <Form className={`${className}__form`}>
            <InputField
              name="name"
              label={t('account_information.name', 'Name')}
              type="text"
            />
            <InputField
              name="surname"
              label={t('account_information.surname', 'Surname')}
              type="text"
            />
            <InputField
              name="email"
              label={t('account_information.email', 'Email')}
              type="email"
              data-testid="input-email"
              disabled
            />
            <InputField
              name="password"
              label={t('account_information.password', 'Password')}
              type="password"
              disabled
            />
            <div className={`${className}__change-password`}>
              <ButtonV2
                data-testid="link-change-password"
                variant="tertiary"
                onClick={changePassword}
              >
                {t('account_information.change_password', 'Change Password')}
              </ButtonV2>
            </div>
            <InputField
              name="phoneNumber"
              label={t('account_information.phone_number', 'Phone Number')}
              type="tel"
              disabled
            />
            <div className={`${className}__change-password`}>
              <ButtonV2
                data-testid="link-change-number"
                variant="tertiary"
                onClick={changePhoneNumber}
              >
                {user?.phone_number
                  ? t(
                      'account_information.change_phone_number',
                      'Change Number',
                    )
                  : t('account_information.add_phone_number', 'Add Number')}
              </ButtonV2>
            </div>
            <div className={`${className}__save-changes`}>
              <SubmitButton />
            </div>
            {errorMessage && <Toast error>{errorMessage}</Toast>}
          </Form>
        </Formik>
      </div>
      {renderChangePasswordModal()}
      {renderChangePhoneNumberModal()}
    </div>
  );
};

export default AccountInformation;
